import { 
  BarChart3, 
  Users, 
  MessageSquare, 
  Calendar, 
  Briefcase, 
  Heart,
  ArrowRight,
  Zap,
  Target,
  Shield
} from 'lucide-react';

export default function CoreFeatures() {
  const features = [
    {
      title: "Community Dashboard",
      icon: BarChart3,
      items: [
        "Comprehensive analytics and insights about your community growth",
        "Real-time activity feeds and member engagement metrics", 
        "Customizable widgets and community health monitoring"
      ]
    },
    {
      title: "Member Management",
      icon: Users,
      items: [
        "Advanced member profiles with skills and interests matching",
        "Role-based permissions and community moderation tools",
        "Smart member discovery and connection recommendations"
      ]
    },
    {
      title: "Engagement Tools",
      icon: MessageSquare,
      items: [
        "Rich discussion forums with threaded conversations",
        "Live chat, polls, and interactive content creation",
        "Gamification features to boost member participation"
      ]
    },
    {
      title: "Events & Meetups",
      icon: Calendar,
      items: [
        "Seamless event planning with RSVP management",
        "Virtual and in-person event hosting capabilities",
        "Automated reminders and post-event follow-ups"
      ]
    },
    {
      title: "Collaboration Hub",
      icon: Briefcase,
      items: [
        "Project collaboration spaces and resource sharing",
        "Skill-based networking and mentorship matching",
        "Job board and opportunity sharing platform"
      ]
    },
    {
      title: "Community Growth",
      icon: Target,
      items: [
        "Referral systems and viral growth mechanisms",
        "Content marketing tools and social media integration",
        "Member retention and engagement optimization"
      ]
    }
  ];

  return (
    <section className="py-20 bg-white" id="coreFeatures">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-purple-600/10 text-purple-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Powerful Features
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Everything You Need To Build 
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Amazing Communities
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive tools designed to help you create, manage, and grow thriving communities
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="group">
              <div className="bg-gray-50 hover:bg-white rounded-2xl p-8 transition-all duration-300 hover:shadow-xl border border-gray-100 hover:border-purple-600/30 h-full">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors">
                      {feature.title}
                    </h3>
                    <ul className="space-y-3">
                      {feature.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <ArrowRight className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Features Banner */}
        <div className="mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-center">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute right-4 top-4 h-32 w-32 rounded-full bg-white blur-2xl" />
            <div className="absolute bottom-4 left-4 h-24 w-24 rounded-full bg-purple-400 blur-xl" />
          </div>
          <div className="relative text-white">
            <div className="flex justify-center mb-6">
              <Shield className="w-12 h-12" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Enterprise-Grade Security & Reliability</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Built with security-first architecture, 99.9% uptime guarantee, and compliance with global data protection standards.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>SOC 2 Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span>GDPR Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span>99.9% Uptime SLA</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
